import React, { useMemo, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { EnrollmentBillingItem } from "@/modules/eventRegistration/features/EventWaitlist/api/getEnrollmentBilling/types";
import { isSendable } from "@/modules/eventRegistration/features/EventWaitlist/components/TransactionsView/TransactionsView";
import { usePaymentRequests } from "../../hooks/usePaymentRequests";
import {
  PaymentRequest,
  PaymentRequestRecipient,
  PaymentRequestStatus,
} from "../../types/paymentRequest";
import { resendPaymentRequest } from "../../api/resendPaymentRequest";
import { cancelPaymentRequest } from "../../api/cancelPaymentRequest";
import { GeneratePaymentLinkModal } from "../GeneratePaymentLinkModal/GeneratePaymentLinkModal";
import { SendPaymentRequestModal } from "../SendPaymentRequestModal/SendPaymentRequestModal";

const STATUS_FILTERS: { label: string; value?: PaymentRequestStatus }[] = [
  { label: "All", value: undefined },
  { label: "Pending", value: "pending" },
  { label: "Completed", value: "completed" },
  { label: "Expired", value: "expired" },
  { label: "Cancelled", value: "cancelled" },
];

interface Props {
  sessionUuid: string;
  /** This session's billing items — used to drive the "Generate" checklist. */
  billingItems: EnrollmentBillingItem[];
  /** Refresh the billing tab after a request is created/resent. */
  onBillingRefresh: () => void;
}

export function SessionPaymentRequestsView({
  sessionUuid,
  billingItems,
  onBillingRefresh,
}: Props) {
  const { paymentRequests, loading, error, status, setStatus, refetch } =
    usePaymentRequests({ sessionUuid });
  const [actingOn, setActingOn] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [itemsToSend, setItemsToSend] = useState<EnrollmentBillingItem[]>([]);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const sendableEnrollments = useMemo(
    () => billingItems.filter(isSendable),
    [billingItems],
  );

  const defaultRecipients: PaymentRequestRecipient[] = useMemo(() => {
    const byEmail = new Map<string, PaymentRequestRecipient>();
    itemsToSend.forEach((item) => {
      const email = item.contactEmail?.trim();
      if (email && !byEmail.has(email)) {
        byEmail.set(email, { email, name: item.contactName?.trim() || "" });
      }
    });
    return Array.from(byEmail.values());
  }, [itemsToSend]);

  const afterChange = () => {
    refetch();
    onBillingRefresh();
  };

  const handleResend = async (uuid: string) => {
    setActingOn(uuid);
    try {
      await resendPaymentRequest(uuid);
      afterChange();
    } finally {
      setActingOn(null);
    }
  };

  const handleCancel = async (uuid: string) => {
    if (
      !window.confirm(
        "Cancel this payment request? The link will stop working.",
      )
    ) {
      return;
    }
    setActingOn(uuid);
    try {
      await cancelPaymentRequest(uuid);
      afterChange();
    } finally {
      setActingOn(null);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-2">
          {STATUS_FILTERS.map((f) => (
            <button
              className={`text-sm px-3 py-1 rounded-md border transition-colors ${
                status === f.value
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-transparent"
                  : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
              }`}
              key={f.label}
              onClick={() => setStatus(f.value)}
              type="button"
            >
              {f.label}
            </button>
          ))}
        </div>
        <Button onClick={() => setPickerOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-1" /> Generate payment link
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : error ? (
        <p className="py-8 text-center text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      ) : (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg divide-y divide-gray-200 dark:divide-slate-700">
          {paymentRequests.length === 0 ? (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No payment requests for this session yet.
            </div>
          ) : null}
          {paymentRequests.map((pr) => (
            <PaymentRequestRow
              actingOn={actingOn}
              key={pr.uuid}
              onCancel={() => handleCancel(pr.uuid)}
              onResend={() => handleResend(pr.uuid)}
              paymentRequest={pr}
            />
          ))}
        </div>
      )}

      <GeneratePaymentLinkModal
        onClose={() => setPickerOpen(false)}
        onContinue={(selected) => {
          setItemsToSend(selected);
          setPickerOpen(false);
          setSendModalOpen(true);
        }}
        open={pickerOpen}
        sendableEnrollments={sendableEnrollments}
      />

      <SendPaymentRequestModal
        defaultRecipients={defaultRecipients}
        enrollments={itemsToSend.map((e) => ({
          enrollmentId: e.enrollmentId,
          attendeeName: `${e.firstName} ${e.lastName}`.trim(),
          amount: e.seatPrice,
        }))}
        onClose={() => setSendModalOpen(false)}
        onSent={() => {
          setSendModalOpen(false);
          afterChange();
        }}
        open={sendModalOpen}
      />
    </div>
  );
}

function PaymentRequestRow({
  paymentRequest: pr,
  onResend,
  onCancel,
  actingOn,
}: {
  paymentRequest: PaymentRequest;
  onResend: () => void;
  onCancel: () => void;
  actingOn: string | null;
}) {
  const { toast } = useToast();
  const canAct = pr.status === "pending" || pr.status === "expired";
  const isActing = actingOn === pr.uuid;

  const recipientLabel =
    pr.recipients.length === 0
      ? "—"
      : pr.recipients.length === 1
        ? pr.recipients[0].name
        : `${pr.recipients[0].name} +${pr.recipients.length - 1} more`;

  const copyLink = async () => {
    const url = `${window.location.origin}/pay/${pr.publicToken}`;
    await navigator.clipboard.writeText(url);
    toast({ title: "Payment link copied" });
  };

  return (
    <div className="px-4 py-3 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {recipientLabel}
          </span>
          <StatusBadge status={pr.status} />
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {pr.recipients.map((r) => r.email).join(", ")}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500">
          {pr.enrollments.length} enrollment
          {pr.enrollments.length === 1 ? "" : "s"} · $
          {Number(pr.amount).toFixed(2)} · sent {pr.sentCount}× · expires{" "}
          {new Date(pr.expiresAt).toLocaleDateString()}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {pr.status === "pending" ? (
          <Button onClick={copyLink} size="sm" variant="ghost">
            Copy payment link
          </Button>
        ) : null}
        {canAct ? (
          <>
            <Button
              disabled={isActing}
              onClick={onResend}
              size="sm"
              variant="outline"
            >
              Resend payment link
            </Button>
            <Button
              disabled={isActing}
              onClick={onCancel}
              size="sm"
              variant="ghost"
            >
              Cancel
            </Button>
          </>
        ) : null}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: PaymentRequestStatus }) {
  const map: Record<
    PaymentRequestStatus,
    { label: string; className: string }
  > = {
    pending: {
      label: "Pending",
      className:
        "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300",
    },
    completed: {
      label: "Paid",
      className:
        "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300",
    },
    expired: {
      label: "Expired",
      className:
        "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300",
    },
    cancelled: {
      label: "Cancelled",
      className:
        "bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-gray-400",
    },
  };
  const { label, className } = map[status];
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${className}`}
    >
      {label}
    </span>
  );
}
